/**
 * Who runs into what.
 *
 * Two separate passes, and they answer different questions. Player against player —
 * `Match::CheckHumanoidCollisions` (match.cpp:1484-1924) — decides who gets shoved off
 * balance and who goes down; it runs at the end of the tick, once every body has been posed.
 * Ball against player — `Match::CheckBallCollisions` (match.cpp:1926-2045) — decides whether
 * a pass or a shot hit somebody on its way, and it runs at the very start of the tick,
 * before the ball moves, reading the poses left over from the tick before.
 */

import { Box3, Vector3 } from 'three';
import { BALL } from '../core/constants';
import { clamp, normalizedClamp, normalizedMax, normalizedOr, normalizedOrZero, rotate2D, vecLength } from '../core/math';
import { hashedRandom } from '../core/random';
import { SPRINT_VELOCITY } from './anim/velocity';
import { TouchType, type Match, type MatchPlayer } from './match';
import type { Skeleton } from './skeleton';

const BOUNCE_RADIUS = 0.36;
const SIMILAR_RADIUS = 0.576;

export interface CollisionBodyPart {
  name: string;
  bounds: Box3;
  /**
   * The joint this part hangs off, in world space — the geometry node's own origin rather
   * than the middle of its box. A deflection pushes the ball away from this point, so a ball
   * against the outside of a shin goes sideways rather than straight back up the shin.
   */
  position: Vector3;
}

export interface CollisionEvent {
  victimId: number;
  tripperId: number;
  tripType: number;
}

interface Bounce {
  opponent: MatchPlayer;
  force: number;
}

export function processPlayerCollisions(match: Match, events: CollisionEvent[]): void {
  // The roster is read once and then indexed, as the original does: it fills a single vector of
  // active players and walks that (`match.cpp:1485-1488`). `match.players` is a getter that
  // builds a fresh twenty-two-element array on every read, and the pair loop below reads it in
  // both loop conditions and twice in its body — about 740 throwaway arrays per tick.
  const players = match.players;
  const bounces = new Map<MatchPlayer, Bounce[]>();
  for (const player of players) bounces.set(player, []);
  for (let a = 0; a < players.length - 1; a++) {
    for (let b = a + 1; b < players.length; b++) {
      collidePair(match, players[a]!, players[b]!, bounces, events);
    }
  }
  for (const player of players) {
    const entries = bounces.get(player)!;
    const total = entries.reduce((sum, entry) => sum + entry.force, 0);
    if (total <= 0) continue;
    const offset = new Vector3();
    for (const entry of entries) {
      offset.addScaledVector(
        entry.opponent.movement.clone().sub(player.movement),
        entry.force * (entry.force / total),
      );
    }
    player.anim.offsetPosition?.(offset.multiplyScalar(0.01));
  }
}

/**
 * Did the ball hit anybody on its way? `Match::CheckBallCollisions` (match.cpp:1926-2045).
 *
 * Without this a shot goes straight through the defender in front of it, which is both the
 * plainest thing wrong with the picture and the reason corners are rare: a corner is almost
 * always a defender getting something on the ball, and here nothing ever did.
 *
 * Three things keep it from firing constantly. It is rate-limited to one contact every 150
 * milliseconds, so a ball rolling along a shin is deflected once rather than every tick. The
 * player must not have touched the ball himself in the last fifth of a second, so his own
 * pass does not immediately rebound off him. And an OPPONENT must have touched it in that
 * time, which is the whole point: a ball your own side played is one you were expecting, and
 * a man is not blocked by a ball he was waiting for.
 *
 * The bodies are the poses left over from the previous tick, because the original runs this
 * before the humanoids update (`match.cpp:868-871`) and everything downstream is calibrated
 * against that lag, the same way every reach test is calibrated against the ball's forecast
 * trailing by a tick.
 */
export function checkBallCollisions(match: Match): void {
  if (match.timeMs <= match.lastBodyBallCollisionTimeMs + 150) return;

  const ball = match.ball.predict(0, _ballNow);
  const bounce = _bounce.set(0, 0, 0);
  let bias = 0;
  let hits = 0;
  let lastHitId = 0;

  for (const player of match.players) {
    if (player.lastTouchBias(200, match.timeMs) > 0.01) continue;
    const opponents = match.opponentTeam(player.teamId);
    if (opponents.lastTouchBias(200, match.timeMs) <= 0.01) continue;
    if (player.hasUniquePossession) continue;

    const type = player.anim.currentFunctionType;
    if (
      type !== 'movement' &&
      type !== 'trip' &&
      type !== 'sliding' &&
      type !== 'interfere' &&
      type !== 'deflect'
    ) {
      continue;
    }

    // A block and a save only count when the ball did something he could not have planned
    // for. He committed to that dive or that outstretched leg a fifth of a second ago
    // against a ball he had already photographed; if the ball has since gone where he
    // thought it would, the animation's own contact handles it and this must not fire twice.
    if (type === 'interfere' || type === 'deflect') {
      const believed = match
        .mentalImage(player.reactionTimeMs + player.anim.frameNum * 10)
        .ballPrediction(1000, _believed);
      if (believed.distanceTo(match.ball.predict(1000, _actual)) <= 0.5) continue;
    }

    // How much of a target he is. Negative shrinks the body, so most of these numbers make
    // a player HARDER to hit than his own outline: a man simply running is nearly
    // transparent, a man throwing himself at the ball is not.
    let margin = -0.1;
    margin += player.hasPossession ? -0.03 : 0.03;
    if (type === 'sliding' || type === 'interfere') margin += 0.1;
    if (type === 'deflect') margin += 0.2;
    const radius = BALL.radius + margin;

    _chest.copy(player.position).setZ(player.position.z + 0.8);
    if (_chest.distanceTo(ball) >= 2.5) continue;

    for (const part of player.collisionBody()) {
      if (part.bounds.distanceToPoint(ball) > radius) continue;

      // The man his own side has nominated to go and win the ball is allowed to take it
      // rather than be bounced off it. The original sets a flag here and then never reads it
      // anywhere (`player.hpp:69-71`), so all the branch does is skip the deflection — and
      // it cannot be reached in any case, because getting this far already required an
      // opponent to have touched the ball within the same 200 ms this asks nobody has.
      // Written out because it is the shape of the original, not because it can happen.
      if (
        player === match.team(player.teamId).designatedPlayer &&
        match.team(match.lastTouchTeamId).lastTouchBias(200, match.timeMs) < 0.01
      ) {
        continue;
      }

      // Which way it comes off him: away from the joint for a ball he is standing in front
      // of, along his own run for a ball an opponent has just struck. The fresher the
      // opponent's touch the more it is the former, because a ball hit a moment ago is
      // arriving fast and rebounds, while one that has been rolling a while is more nudged
      // than struck.
      const movementBias = opponents.lastTouchBias(200, match.timeMs) * 0.8 + 0.2;
      bounce
        .addScaledVector(normalizedOrZero(_partVec.copy(ball).sub(part.position)), movementBias)
        .addScaledVector(player.movement, 1 - movementBias);
      hits++;
      lastHitId = player.id;
      match.setLastTouchPlayer(player, TouchType.Accidental);

      // A ball dead-centre on the part comes off hard, one clipping its edge barely at all.
      part.bounds.getCenter(_centre);
      const partRadius = vecLength(part.bounds.getSize(_size)) / 2;
      bias += (1 - clamp((ball.distanceTo(_centre) - radius) / partRadius, 0, 1)) * 0.9 + 0.1;
    }
  }

  if (bias <= 0) return;

  bounce.divideScalar(hits);
  bounce.z *= 0.6;
  normalizedOrZero(bounce, bounce);
  const current = match.ball.momentum;
  const speed = vecLength(current);
  const struck = _struck
    .copy(bounce)
    .multiplyScalar(6 + speed * 0.6)
    .addScaledVector(current, -0.2);
  const blend = clamp(bias, 0, 1) * 0.5 + 0.5;
  const result = struck.multiplyScalar(blend).addScaledVector(current, 1 - blend);
  // A body can take pace off the ball but never put any on.
  if (vecLength(result) > speed) normalizedOrZero(result, result).multiplyScalar(speed);
  result.multiplyScalar(0.7);

  const tick = (match.timeMs / 10) | 0;
  const spin = (salt: number): number =>
    hashedRandom(tick, lastHitId, salt, match.randomSeed) * 60 - 30;
  match.ball.touch(result);
  // The spin blend reads the same remapped bias the bounce did, not the raw sum.
  match.ball.setRotation(spin(1), spin(2), spin(3), 0.5 * blend);
  match.lastBodyBallCollisionTimeMs = match.timeMs;
}

function collidePair(
  match: Match,
  p1: MatchPlayer,
  p2: MatchPlayer,
  bounces: Map<MatchPlayer, Bounce[]>,
  events: CollisionEvent[],
): void {
  const distance = p1.position.distanceTo(p2.position);
  let bounce = new Vector3();
  let p1BackFacing = 0.5;
  let p2BackFacing = 0.5;
  if (distance < (BOUNCE_RADIUS + SIMILAR_RADIUS) * 2) {
    bounce = normalizedOr(p1.position.clone().sub(p2.position), new Vector3(0, -1, 0));
    const facing1 = rotate2D(p1.directionVec, p1.anim.spatial.relBodyAngle * 0.7);
    const facing2 = rotate2D(p2.directionVec, p2.anim.spatial.relBodyAngle * 0.7);
    p1BackFacing = clamp(facing1.dot(bounce) * 0.5 + 0.5, 0, 1);
    p2BackFacing = clamp(facing2.dot(bounce.clone().negate()) * 0.5 + 0.5, 0, 1);

    if (distance < BOUNCE_RADIUS * 2) {
      applySeparation(match, p1, p2, distance, p1BackFacing, p2BackFacing);
      maybeTrip(match, p1, p2, bounce, p1BackFacing, events);
      maybeTrip(match, p2, p1, bounce.clone().negate(), p2BackFacing, events);
    }

    const shellDistance = Math.max(0, distance - BOUNCE_RADIUS * 2);
    let similarBias = (p1BackFacing - p2BackFacing) * 0.8;
    similarBias -= clamp(((p1.floatVelocity - p2.floatVelocity) / SPRINT_VELOCITY) * 0.2, -0.2, 0.2);
    if (p1 === match.designatedPossessionPlayer) similarBias += 0.6;
    if (p2 === match.designatedPossessionPlayer) similarBias -= 0.6;
    similarBias += ballClosenessBias(match, p1, p2);
    similarBias += p1.physics.stat('physical_balance') - p2.physics.stat('physical_balance');
    similarBias = clamp(similarBias, -1, 1) * 0.9;
    let force = clamp(1 - shellDistance / (SIMILAR_RADIUS * 2), 0, 1);
    force = Math.pow(force, 0.2) * 0.25;
    const bias01 = similarBias * 0.5 + 0.5;
    bounces.get(p1)!.push({ opponent: p2, force: force * (1 - bias01) });
    bounces.get(p2)!.push({ opponent: p1, force: force * bias01 });
  }

  tackleCollision(match, p1, p2, distance, events);
}

function applySeparation(
  match: Match,
  p1: MatchPlayer,
  p2: MatchPlayer,
  distance: number,
  p1BackFacing: number,
  p2BackFacing: number,
): void {
  let bias = (p1BackFacing - p2BackFacing) * 0.8;
  bias -= clamp(((p1.floatVelocity - p2.floatVelocity) / SPRINT_VELOCITY) * 0.2, -0.2, 0.2);
  if (p1.touchPending() && p1.anim.currentFunctionType === 'interfere') bias += 0.1 + 0.4 * p1.physics.stat('technical_standingtackle');
  if (p1.touchPending() && p1.anim.currentFunctionType === 'sliding') bias += 0.1 + 0.4 * p1.physics.stat('technical_slidingtackle');
  if (p2.touchPending() && p2.anim.currentFunctionType === 'interfere') bias -= 0.1 + 0.4 * p2.physics.stat('technical_standingtackle');
  if (p2.touchPending() && p2.anim.currentFunctionType === 'sliding') bias -= 0.1 + 0.4 * p2.physics.stat('technical_slidingtackle');
  if (p1 === match.designatedPossessionPlayer) bias += 0.4;
  if (p2 === match.designatedPossessionPlayer) bias -= 0.4;
  bias += ballClosenessBias(match, p1, p2);
  bias += p1.physics.stat('physical_balance') - p2.physics.stat('physical_balance');
  const bias01 = clamp(bias, -1, 1) * 0.25 + 0.5;
  const direction12 = normalizedOrZero(p1.position.clone().sub(p2.position));
  const direction21 = normalizedOrZero(p2.position.clone().sub(p1.position));
  const p1Offset = direction12.multiplyScalar((BOUNCE_RADIUS - distance * 0.5) * (1 - bias01) * 2);
  const p2Offset = direction21.multiplyScalar((BOUNCE_RADIUS - distance * 0.5) * bias01 * 2);

  if (match.designatedPossessionPlayer === p2 && p2.hasPossession) {
    addSideSnap(p1, p2, p1Offset);
  } else if (match.designatedPossessionPlayer === p1 && p1.hasPossession) {
    addSideSnap(p2, p1, p2Offset);
  }
  p1.anim.offsetPosition?.(normalizedMax(p1Offset, SPRINT_VELOCITY * 0.01));
  p2.anim.offsetPosition?.(normalizedMax(p2Offset, SPRINT_VELOCITY * 0.01));
}

function addSideSnap(chaser: MatchPlayer, owner: MatchPlayer, offset: Vector3): void {
  const left = owner.position.clone().addScaledVector(rotate2D(owner.directionVec, 0.3 * Math.PI), BOUNCE_RADIUS * 2);
  const right = owner.position.clone().addScaledVector(rotate2D(owner.directionVec, -0.3 * Math.PI), BOUNCE_RADIUS * 2);
  const side = chaser.position.distanceTo(left) < chaser.position.distanceTo(right) ? left : right;
  offset.addScaledVector(normalizedMax(side.sub(chaser.position), 0.01), chaser.physics.stat('physical_balance') * 0.3);
}

function ballClosenessBias(match: Match, p1: MatchPlayer, p2: MatchPlayer): number {
  if (match.team(p1.teamId).designatedPlayer !== p1 || match.team(p2.teamId).designatedPlayer !== p2) return 0;
  const ball = match.ball.predict(10, _ball).setZ(0);
  return clamp(Math.min(p2.position.distanceTo(ball), 1.2) - Math.min(p1.position.distanceTo(ball), 1.2), -0.6, 0.6);
}

function maybeTrip(
  match: Match,
  victim: MatchPlayer,
  other: MatchPlayer,
  bounce: Vector3,
  backFacing: number,
  events: CollisionEvent[],
): void {
  const penetration = victim.position
    .clone()
    .addScaledVector(victim.movement, 0.03)
    .distanceTo(other.position.clone().addScaledVector(other.movement, 0.03));
  const ballDistance = victim.position.distanceTo(match.ball.predict(10, _ball).setZ(0));
  let sensitivity = 1 - backFacing;
  sensitivity += normalizedClamp(victim.floatVelocity, 0, SPRINT_VELOCITY);
  if (victim.hasBestPossession) sensitivity += 1;
  sensitivity += (1 - victim.physics.stat('physical_balance')) * 3;
  sensitivity += clamp(victim.physics.decayingPositionOffset.length() * 10, 0, 1);
  sensitivity += Math.pow(1 - normalizedClamp(penetration, 0, BOUNCE_RADIUS * 2), 0.4) * 6;
  sensitivity += 1 - normalizedClamp(ballDistance, 0, 0.7);
  sensitivity /= 14;
  let type = 0;
  if (sensitivity > 0.48) type = 1;
  if (sensitivity > 0.58) type = 2;
  if (type === 0) return;
  const direction = normalizedOr(
    victim.movement.clone().multiplyScalar(0.1).addScaledVector(other.movement, 0.06).add(bounce),
    bounce,
  );
  victim.anim.trip?.(direction, type);
  events.push({ victimId: victim.id, tripperId: other.id, tripType: type });
}

function tackleCollision(
  match: Match,
  p1: MatchPlayer,
  p2: MatchPlayer,
  distance: number,
  events: CollisionEvent[],
): void {
  const p1Tackle = isTackling(p1);
  const p2Tackle = isTackling(p2);
  if (distance >= 2 || p1Tackle === p2Tackle) return;
  const tackler = p1Tackle ? p1 : p2;
  const victim = p1Tackle ? p2 : p1;
  if (tackler.anim.frameNum <= 10 || tackler.anim.frameNum >= tackler.anim.clip.frameCount - 6) return;
  const violent = isViolentAttacker(tackler, match);
  const tacklerParts = tackler.collisionBody();
  // An honest tackle can only ever catch a leg — that is the whole shape of the geometry
  // check below. A violent hit is not aimed at the ball at all, so it is not confined to the
  // legs either: the flying kick's own clip, once it is its own animation rather than
  // borrowing the sliding tackle's twelve (tranche 2.1), reaches the chest on purpose. See
  // `isViolentAttacker`.
  const victimParts = violent
    ? victim.collisionBody()
    : victim
        .collisionBody()
        .filter((part) => ['left_foot', 'right_foot', 'left_lowerleg', 'right_lowerleg'].includes(part.name));
  if (tacklerParts.length === 0 || victimParts.length === 0) return;
  const hit = tacklerParts.some((part) => {
    const shrunk = part.bounds.clone().expandByScalar(-0.1);
    return victimParts.some((victimPart) => shrunk.intersectsBox(victimPart.bounds));
  });
  if (!hit) return;
  // A STANDING TACKLE THAT FELLS A MAN IS A FOUL, and for fifteen years it was not.
  //
  // The original's author wrote `if (Interfere) tripType = 1; // was 2` (`match.cpp:1899`),
  // and the referee only ever judges a trip of type 2 or 3 — so of every standing challenge
  // in which one player's boot actually went through another's legs, not one was ever given.
  // Fouls could then only come from sliding tackles, which are rare by design, and the game
  // awarded sixteen times fewer free kicks than football does. The comment is the only record
  // of why the number was changed, and it says nothing; the behaviour it produces is plainly
  // wrong, so it goes back.
  //
  // What follows from the 2 is not "every contact is a foul". The referee still asks that the
  // victim's side clearly have the ball, that he be within two metres of it, and that the
  // tackler be an opponent in a tackling animation (`referee.cpp:325-339`), and it is given
  // as an advantage rather than an immediate whistle.
  const type = tackler.anim.currentFunctionType === 'interfere' ? 2 : 3;
  // A VIOLENT HIT IS NOT A TRIP — tranche 2.3 of docs/design/violence-and-variety.md. The
  // whole `trip` family is a man whose LEGS met another man's; being flying-kicked or
  // punched is not that, so this plays the struck reaction instead. The foul bookkeeping
  // below (the event, the referee's own read of `violentChallengeUntilMs`) is unaffected —
  // this only changes which animation the victim plays, never whether it is a foul.
  if (violent) {
    victim.anim.struck?.(victim.position.clone().sub(tackler.position).setZ(0));
  } else {
    victim.anim.trip?.(victim.directionVec, type);
  }
  events.push({ victimId: victim.id, tripperId: tackler.id, tripType: type });
}

function isTackling(player: MatchPlayer): boolean {
  const type = player.anim.currentFunctionType;
  if (
    (type === 'sliding' || type === 'interfere') &&
    player.anim.frameNum > 5 &&
    player.anim.frameNum < 28
  ) {
    return true;
  }
  // A punch or a flying kick drawn as its OWN clip — `specialvar1` 4 or 5 — reaches an
  // opponent through this same posed-body-overlap test rather than through the twelve
  // sliding clips. Neither is wired to a command yet (tranches 2.1/2.2), so this branch is
  // presently unreachable in play; it costs nothing to have ready, and it is what
  // `isViolentAttacker` below relies on.
  if (type === 'special') {
    const specialVar1 = player.anim.clip.vars['specialvar1'];
    return specialVar1 === '4' || specialVar1 === '5';
  }
  return false;
}

/**
 * Is this tackler, right now, committing a punch or a flying kick rather than an honest
 * challenge? Two signals, because only one of them is reachable today.
 *
 * `violentChallengeUntilMs` is the flying kick's own flag (`controller.ts`, set by nothing
 * else) and is the live one: the flying kick still draws from the twelve sliding clips
 * (2.1 is not wired), so a `sliding` function type alone cannot tell a lunge at a man from
 * an honest tackle, and this is how the collision pass is told which it is — the SAME way
 * the referee already is (`referee.ts:293`), independently, off the same field.
 *
 * The `special`-clip check exists for the day 2.1 and 2.2 land and a punch or a flying kick
 * becomes its own animation with `specialvar1` 4 or 5: `isTackling` already accepts such a
 * clip, so this only needs to say which of the two accepted cases is the violent one.
 */
function isViolentAttacker(attacker: MatchPlayer, match: Match): boolean {
  if (attacker.violentChallengeUntilMs > match.timeMs) return true;
  if (attacker.anim.currentFunctionType !== 'special') return false;
  const specialVar1 = attacker.anim.clip.vars['specialvar1'];
  return specialVar1 === '4' || specialVar1 === '5';
}

/**
 * Which meshes have already been complained about, so a fault is said once and not two
 * thousand times a second.
 */
const missingBounds = new Set<string>();

/** Build the body-part AABBs the collision path needs from the already posed skeleton. */
export function collisionBodyFromSkeleton(
  skeleton: Skeleton,
  origin: Vector3,
  heightScale: number,
): CollisionBodyPart[] {
  const result: CollisionBodyPart[] = [];
  for (let i = 0; i < skeleton.names.length; i++) {
    const name = skeleton.geometryNames[i];
    const file = skeleton.geometryFiles[i];
    // A joint with no mesh at all is ordinary — the root has none. A joint that names a mesh
    // this table has never heard of is a fault, and it is the kind that hides: the limb simply
    // stops existing for every tackle in the game, nothing errors, and the symptom is that
    // challenges mysteriously stop connecting. That exact shape of failure has already cost
    // this port every sliding tackle and every foul once.
    if (!name || !file) continue;
    const local = GEOMETRY_BOUNDS[file];
    if (!local) {
      if (!missingBounds.has(file)) {
        missingBounds.add(file);
        console.warn(
          `[gf] no collision bounds for "${file}", so "${name}" cannot be tackled or ` +
            `tackled with. Add it to GEOMETRY_BOUNDS in src/sim/collision.ts.`,
        );
      }
      continue;
    }
    const bounds = new Box3();
    bounds.makeEmpty();
    for (const x of [local.min.x, local.max.x]) {
      for (const y of [local.min.y, local.max.y]) {
        for (const z of [local.min.z, local.max.z]) {
          bounds.expandByPoint(
            new Vector3(x, y, z)
              .applyQuaternion(skeleton.worldRotation[i]!)
              .add(skeleton.worldPosition[i]!)
              .multiplyScalar(heightScale)
              .add(origin),
          );
        }
      }
    }
    result.push({
      name,
      bounds,
      position: skeleton.worldPosition[i]!.clone().multiplyScalar(heightScale).add(origin),
    });
  }
  return result;
}

// Exact local bounds of the eight body-part ASE meshes. GeometryData calculates these from
// the same vertices; caching the extrema avoids shipping eight otherwise-undrawn meshes.
const GEOMETRY_BOUNDS: Readonly<Record<string, Box3>> = {
  'models/pelvis.ase': box(-0.1958, -0.1131, -0.0622, 0.1959, 0.1271, 0.2318),
  'models/trunk.ase': box(-0.229, -0.13, -0.082, 0.229, 0.117, 0.525),
  'models/head.ase': box(-0.0881, -0.1302, -0.0448, 0.0887, 0.106, 0.2872),
  'models/upperarm.ase': box(-0.059, -0.07, -0.34, 0.059, 0.071, -0.044),
  'models/lowerarm.ase': box(-0.0393, -0.0519, -0.3414, 0.0387, 0.0404, 0.0359),
  'models/upperleg.ase': box(-0.1013, -0.0894, -0.4543, 0.1013, 0.1051, 0.0784),
  'models/lowerleg.ase': box(-0.059, -0.0494, -0.4972, 0.0604, 0.0846, 0.0448),
  'models/foot.ase': box(-0.0412, -0.1607, -0.1139, 0.0419, 0.0961, -0.0001),
};

function box(
  minX: number,
  minY: number,
  minZ: number,
  maxX: number,
  maxY: number,
  maxZ: number,
): Box3 {
  return new Box3(new Vector3(minX, minY, minZ), new Vector3(maxX, maxY, maxZ));
}

const _ball = new Vector3();
const _ballNow = new Vector3();
const _bounce = new Vector3();
const _believed = new Vector3();
const _actual = new Vector3();
const _chest = new Vector3();
const _partVec = new Vector3();
const _centre = new Vector3();
const _size = new Vector3();
const _struck = new Vector3();
